import { v4 as uuidv4 } from 'uuid';

import { distance as getDistance } from 'utils/utils';
import type { ProviderData, ProviderLocationData, SpecialtyData } from 'types/common.types';

import { MAX_RADIUS_VALUE, RADIUS_VALUES } from '../filters/filterConstants';

/**
 * Search parameters for formatting
 */
interface SearchParameters {
  specialties?: SpecialtyData[];
  subspecialties?: SpecialtyData[];
  radius?: number;
  location?: {
    latitude: number;
    longitude: number;
  };
  lgbtqCompetent?: boolean;
  lookingForDiagnosis?: boolean;
  lookingForTreatment?: boolean;
  gender?: string;
  languagesSpoken?: string[];
  city?: string;
  stateAbbreviation?: string;
}

/**
 * Formatted search parameters
 */
interface FormattedSearchParameters {
  specialties?: SpecialtyData[];
  subspecialties?: SpecialtyData[];
  radius?: number;
  location?: {
    latitude: number;
    longitude: number;
  };
  lgbtqCompetent?: boolean;
  lookingForDiagnosis?: boolean;
  lookingForTreatment?: boolean;
  gender?: string;
  languagesSpoken?: string;
  city?: string;
  stateAbbreviation?: string;
}

/**
 * Chat response parameters with lat/lon
 */
interface ChatResponseParameters {
  location: {
    lat: number;
    lon: number;
  };
  radiusInMiles?: number;
  [key: string]: unknown;
}

/**
 * Formatted chat response parameters with latitude/longitude
 */
interface FormattedChatResponseParameters {
  location: {
    latitude: number;
    longitude: number;
  };
  radius: number;
  [key: string]: unknown;
}

/**
 * Parameters for building API URL
 */
interface BuildApiUrlParams {
  radius?: number;
  specialtyId?: number;
  subspecialtyId?: number;
  lgbtqCompetent?: boolean;
  lookingForDiagnosis?: boolean;
  lookingForTreatment?: boolean;
  gender?: string;
  languagesSpoken?: string;
  location: {
    latitude: number;
    longitude: number;
  };
  networkSlug: string;
}

interface BuildApiUrlOptions {
  endpoint: string;
  params?: Partial<BuildApiUrlParams>;
  closestLocationSearch?: boolean;
}

/**
 * Coordinates for location
 */
interface Coordinates {
  latitude: number;
  longitude: number;
}

/**
 * Specialty object that may have subspecialtyId
 */
interface SpecialtyWithSubspecialty extends SpecialtyData {
  subspecialtyId?: number;
}

// @TODO: add tests
export const formatParametersForSearch = (params: SearchParameters): FormattedSearchParameters => ({
  specialties: params.specialties,
  subspecialties: params.subspecialties,
  radius: params.radius,
  location: params.location,
  lgbtqCompetent: params.lgbtqCompetent,
  lookingForDiagnosis: params.lookingForDiagnosis,
  lookingForTreatment: params.lookingForTreatment,
  gender: params.gender,
  languagesSpoken: params.languagesSpoken?.join(),
  city: params.city,
  stateAbbreviation: params.stateAbbreviation,
});

export const formatChatResponseParameters = (
  params: ChatResponseParameters
): FormattedChatResponseParameters => {
  // chat returns lat and lon, reassign to latitude and longitude
  // also reformat radiusInMiles to radius, and delete radiusInMiles property
  const formattedParams: FormattedChatResponseParameters = {
    ...params,
    location: {
      latitude: params.location.lat,
      longitude: params.location.lon,
    },
    radius: params.radiusInMiles || 25,
  };
  delete formattedParams.radiusInMiles;
  return formattedParams;
};

export const buildApiUrl = ({
  endpoint,
  params = {},
  closestLocationSearch,
}: BuildApiUrlOptions): string => {
  const {
    radius,
    specialtyId,
    subspecialtyId,
    lgbtqCompetent,
    lookingForDiagnosis,
    lookingForTreatment,
    gender,
    languagesSpoken,
    location,
    networkSlug,
  } = params;

  if (!location?.latitude || !location?.longitude || !networkSlug) {
    throw new Error('location (with latitude and longitude) and networkSlug are required');
  }

  const ordering = [
    'best_match',
    lgbtqCompetent && '-outcare_competent',
    lookingForDiagnosis && '-diagnosing_percentile',
    lookingForTreatment && '-treatment_percentile',
  ]
    .filter(Boolean)
    .join(',');

  return (
    `/${endpoint}/?limit=3&ordering=${ordering}` +
    `&location=${location.latitude},${location.longitude}` +
    `&radius=${closestLocationSearch ? MAX_RADIUS_VALUE : radius}mi` +
    `&network_slug=${networkSlug}` +
    `${gender ? `&gender=${gender}` : ''}` +
    `${languagesSpoken ? `&languages=${languagesSpoken}` : ''}` +
    `${specialtyId ? `&specialty_id=${specialtyId}` : ''}` +
    `${subspecialtyId ? `&subspecialty_id=${subspecialtyId}` : ''}`
  );
};

// Used for sorting all provider places when no location is used in query
export const sortProviderPlacesByDistance = (
  places: ProviderLocationData[],
  lat: number,
  long: number
): ProviderLocationData[] =>
  places.sort(
    (a, b) =>
      getDistance(lat, long, a.latitude ?? 0, a.longitude ?? 0) -
      getDistance(lat, long, b.latitude ?? 0, b.longitude ?? 0)
  );

export const formatProviderPlaces = (
  provider: ProviderData | null | undefined,
  coords: Coordinates | null | undefined
): ProviderData | null => {
  if (!provider || !coords) return null;
  const formattedProvider: ProviderData = { ...provider };
  const places = provider.places || [];
  formattedProvider.places = places.map((place) => ({ ...place, id: uuidv4() }));
  const sortedPlaces = sortProviderPlacesByDistance(places, coords.latitude, coords.longitude);
  // eslint-disable-next-line prefer-destructuring
  formattedProvider.closestVisibleLocation = sortedPlaces[0];

  return formattedProvider;
};

export const grammaticalCsl = (list: string[], separator = 'and'): string | null => {
  if (list.length === 0) return null;
  if (list.length === 1) return list[0];
  // create array of labels,
  const copy = [...list];
  const last = copy.pop(); // returns last item, removes it from 'labels'
  return `${copy.join(', ')} ${separator} ${last}`;
};

/**
 * @param distance Distance specified (in this case by the Chat user).
 * @returns A preset radius value that the user supplied value is rounded *up* to
 */
export const findSmallestRadius = (distance: number | null | undefined): number | null => {
  if (!distance) return null;
  if (distance > MAX_RADIUS_VALUE) return null;
  const radiusObj = RADIUS_VALUES.find((r) => distance <= r.value);
  return radiusObj?.value ?? null;
};

/**
 * @param point1 Latlong pair
 * @param point2 Latlong pair
 * @returns A preset radius value that the user supplied value is rounded *up* to
 */
export const findSmallestRadiusBetweenPoints = (
  point1: Coordinates | null | undefined,
  point2: Coordinates | null | undefined
): number | null => {
  if (!point1 || !point2) return null;
  return findSmallestRadius(
    getDistance(point1.latitude, point1.longitude, point2.latitude, point2.longitude)
  );
};

const isPrimaryCareSpecialty = (specialtyObj: Partial<SpecialtyWithSubspecialty> = {}): boolean =>
  specialtyObj?.specialtyId === 5 && !specialtyObj.subspecialtyId;

export function sortPrimaryCareToFront(
  specialtyArray: SpecialtyWithSubspecialty[]
): SpecialtyWithSubspecialty[] {
  return specialtyArray.sort((a, b) => {
    if (isPrimaryCareSpecialty(a)) return -1;
    if (isPrimaryCareSpecialty(b)) return 1;
    return 0;
  });
}
